import { DOCUMENT, isPlatformBrowser, NgTemplateOutlet } from '@angular/common'
import {
  AfterContentInit,
  AnimationCallbackEvent,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChildren,
  ElementRef,
  numberAttribute,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  input,
  inject,
  output,
} from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { RouterLink, RouterLinkActive } from '@angular/router'
import { MenuItem, OverlayService, PrimeNGConfig, PrimeTemplate } from '../api'
import { ConnectedOverlayScrollHandler, DomHandler } from '../dom'

import { Nullable, VoidListener } from '../ts-helpers'
import { UniqueComponentId, ZIndexUtils } from '../utils'
import { Tooltip } from '../tooltip/tooltip'
import { Ripple } from '../ripple/ripple'

@Pipe({ name: 'safeHtml' })
export class SafeHtmlPipe implements PipeTransform {
  private readonly platformId = inject(PLATFORM_ID)
  private readonly sanitizer = inject(DomSanitizer)

  public transform(value: string): SafeHtml {
    if (!value || !isPlatformBrowser(this.platformId)) {
      return value
    }

    return this.sanitizer.bypassSecurityTrustHtml(value)
  }
}

@Component({
  selector: '[pMenuItemContent]',
  template: `
    <div
      [attr.data-pc-section]="'content'"
      class="p-menuitem-content"
      (click)="onItemClick($event, item())"
    >
      @if (!itemTemplate()) {
        @if (!item()?.routerLink) {
          <a
            [attr.title]="item().title"
            [attr.href]="item().url || null"
            [attr.data-automationid]="item().automationId"
            [attr.tabindex]="-1"
            [attr.data-pc-section]="'action'"
            class="p-menuitem-link"
            [target]="item().target"
            [class.p-disabled]="item().disabled"
            pRipple
          >
            <ng-container *ngTemplateOutlet="itemContent; context: { $implicit: item() }" />
          </a>
        }
        @if (item()?.routerLink) {
          <a
            [routerLink]="item().routerLink"
            [attr.data-automationid]="item().automationId"
            [attr.tabindex]="-1"
            [attr.data-pc-section]="'action'"
            [attr.title]="item().title"
            [queryParams]="item().queryParams"
            routerLinkActive="p-menuitem-link-active"
            [routerLinkActiveOptions]="item().routerLinkActiveOptions || { exact: false }"
            class="p-menuitem-link"
            [target]="item().target"
            [class.p-disabled]="item().disabled"
            [fragment]="item().fragment"
            [queryParamsHandling]="item().queryParamsHandling"
            [preserveFragment]="item().preserveFragment"
            [skipLocationChange]="item().skipLocationChange"
            [replaceUrl]="item().replaceUrl"
            [state]="item().state"
            pRipple
          >
            <ng-container *ngTemplateOutlet="itemContent; context: { $implicit: item() }" />
          </a>
        }
      }

      @if (itemTemplate()) {
        <ng-template *ngTemplateOutlet="itemTemplate(); context: { $implicit: item() }" />
      }

      <ng-template #itemContent>
        @if (item().icon) {
          <span
            class="p-menuitem-icon"
            [class]="[item().iconClass, item().icon].join(' ')"
            [style]="item().iconStyle"
          ></span>
        }
        @if (item().escape !== false) {
          <span class="p-menuitem-text">{{ item().label }}</span>
        } @else {
          <span class="p-menuitem-text" [innerHTML]="item().label | safeHtml"></span>
        }
        @if (item().badge) {
          <span class="p-menuitem-badge" [class]="item().badgeStyleClass">{{ item().badge }}</span>
        }
      </ng-template>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'p-element',
  },
  imports: [Ripple, NgTemplateOutlet, RouterLinkActive, RouterLink, SafeHtmlPipe],
})
export class MenuItemContent {
  readonly item = input<MenuItem | undefined>(undefined, { alias: 'pMenuItemContent' })

  readonly itemTemplate = input<TemplateRef<any> | undefined>(undefined)

  readonly onMenuItemClick = output<any>()

  menu: Menu

  constructor() {
    const menu = inject(Menu)

    this.menu = menu as Menu
  }

  onItemClick(event, item) {
    this.onMenuItemClick.emit({ originalEvent: event, item })
  }
}
/**
 * Menu is a navigation / command component that supports dynamic and static positioning.
 * @group Components
 */
@Component({
  selector: 'p-menu',
  template: `
    @if (!popup() || visible) {
      <div
        #container
        [class]="'p-menu p-component' + (styleClass() ? ' ' + styleClass() : '')"
        [class.p-menu-overlay]="popup()"
        [class.p-menu-overlay-entered]="menuOverlayEntered()"
        [style]="style()"
        (click)="onOverlayClick($event)"
        [style.--p-menu-show-transition]="showTransitionOptions()"
        [style.--p-menu-hide-transition]="hideTransitionOptions()"
        (animate.enter)="onOverlayEnter($event)"
        [animate.leave]="'p-menu-overlay-leave-active'"
        (animate.leave)="onOverlayLeave($event)"
        [attr.data-pc-name]="'menu'"
        [attr.id]="id()"
      >
        @if (startTemplate) {
          <div class="p-menu-start" [attr.data-pc-section]="'start'">
            <ng-container *ngTemplateOutlet="startTemplate" />
          </div>
        }

        <ul
          #list
          class="p-menu-list p-reset"
          role="menu"
          [attr.id]="id() + '_list'"
          [attr.tabindex]="getTabIndexValue()"
          [attr.data-pc-section]="'menu'"
          [attr.aria-activedescendant]="activedescendant()"
          [attr.aria-label]="ariaLabel()"
          [attr.aria-labelledBy]="ariaLabelledBy()"
          (focus)="onListFocus($event)"
          (blur)="onListBlur($event)"
          (keydown)="onListKeyDown($event)"
        >
          @if (hasSubMenu()) {
            @for (submenu of model(); track submenu; let i = $index) {
              @if (submenu.separator) {
                <li
                  class="p-menuitem-separator"
                  [class.p-hidden]="submenu.visible === false"
                  role="separator"
                ></li>
              } @else {
                <li
                  class="p-submenu-header"
                  [attr.data-automationid]="submenu.automationId"
                  [class]="{ 'p-hidden': submenu.visible === false, flex: submenu.visible }"
                  pTooltip
                  [tooltipOptions]="submenu.tooltipOptions"
                  role="none"
                  [attr.id]="menuitemId(submenu, id(), i)"
                >
                  @if (!submenuHeaderTemplate) {
                    @if (submenu.escape !== false) {
                      <span>{{ submenu.label }}</span>
                    } @else {
                      <span [innerHTML]="submenu.label | safeHtml"></span>
                    }
                  }
                  <ng-container
                    *ngTemplateOutlet="submenuHeaderTemplate; context: { $implicit: submenu }"
                  />
                </li>
              }

              @for (item of submenu.items; track item; let j = $index) {
                @if (item.separator) {
                  <li
                    class="p-menuitem-separator"
                    [class.p-hidden]="item.visible === false || submenu.visible === false"
                    role="separator"
                  ></li>
                } @else {
                  <li
                    [pMenuItemContent]="item"
                    [itemTemplate]="itemTemplate"
                    [class]="'p-menuitem' + (item.styleClass ? ' ' + item.styleClass : '')"
                    [class.p-hidden]="item.visible === false || submenu.visible === false"
                    [class.p-focus]="
                      focusedOptionId() && menuitemId(item, id(), i, j) === focusedOptionId()
                    "
                    [class.p-disabled]="disabled(item.disabled)"
                    [style]="item.style"
                    (onMenuItemClick)="itemClick($event, menuitemId(item, id(), i, j))"
                    pTooltip
                    [tooltipOptions]="item.tooltipOptions"
                    role="menuitem"
                    [attr.data-pc-section]="'menuitem'"
                    [attr.aria-label]="label(item.label)"
                    [attr.data-p-focused]="isItemFocused(menuitemId(item, id(), i, j))"
                    [attr.data-p-disabled]="disabled(item.disabled)"
                    [attr.aria-disabled]="disabled(item.disabled)"
                    [attr.id]="menuitemId(item, id(), i, j)"
                  ></li>
                }
              }
            }
          } @else {
            @for (item of model(); track item; let i = $index) {
              @if (item.separator) {
                <li
                  class="p-menuitem-separator"
                  [class.p-hidden]="item.visible === false"
                  role="separator"
                ></li>
              } @else {
                <li
                  [pMenuItemContent]="item"
                  [itemTemplate]="itemTemplate"
                  [class]="'p-menuitem' + (item.styleClass ? ' ' + item.styleClass : '')"
                  [class.p-hidden]="item.visible === false"
                  [class.p-focus]="
                    focusedOptionId() && menuitemId(item, id(), i) === focusedOptionId()
                  "
                  [class.p-disabled]="disabled(item.disabled)"
                  [style]="item.style"
                  (onMenuItemClick)="itemClick($event, menuitemId(item, id(), i))"
                  pTooltip
                  [tooltipOptions]="item.tooltipOptions"
                  role="menuitem"
                  [attr.data-pc-section]="'menuitem'"
                  [attr.aria-label]="label(item.label)"
                  [attr.data-p-focused]="isItemFocused(menuitemId(item, id(), i))"
                  [attr.data-p-disabled]="disabled(item.disabled)"
                  [attr.aria-disabled]="disabled(item.disabled)"
                  [attr.id]="menuitemId(item, id(), i)"
                ></li>
              }
            }
          }
        </ul>

        @if (endTemplate) {
          <div class="p-menu-end" [attr.data-pc-section]="'end'">
            <ng-container *ngTemplateOutlet="endTemplate" />
          </div>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./menu.css'],
  host: {
    class: 'p-element',
  },
  imports: [NgTemplateOutlet, Tooltip, MenuItemContent, SafeHtmlPipe],
})
export class Menu implements OnDestroy, OnInit, AfterContentInit {
  private document = inject<Document>(DOCUMENT)
  private platformId = inject(PLATFORM_ID)
  el = inject(ElementRef)
  renderer = inject(Renderer2)
  private cd = inject(ChangeDetectorRef)
  config = inject(PrimeNGConfig)
  overlayService = inject(OverlayService)

  /**
   * An array of menuitems.
   * @group Props
   */
  readonly model = input<
    MenuItem[] | undefined
    /**
     * Defines if menu would displayed as a popup.
     * @group Props
     */
  >(undefined)
  /**
   * Defines if menu would displayed as a popup.
   * @group Props
   */
  readonly popup = input<
    boolean | undefined,
    /**
     * Inline style of the component.
     * @group Props
     */
    unknown
  >(undefined, { transform: booleanAttribute })
  /**
   * Inline style of the component.
   * @group Props
   */
  readonly style = input<
    Record<string, any> | null | undefined
    /**
     * Style class of the component.
     * @group Props
     */
  >(undefined)
  /**
   * Style class of the component.
   * @group Props
   */
  readonly styleClass = input<
    string | undefined
    /**
     * Target element to attach the overlay, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
     * @group Props
     */
  >(undefined)
  /**
   * Target element to attach the overlay, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
   * @group Props
   */
  readonly appendTo = input<
    HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any
    /**
     * Whether to automatically manage layering.
     * @group Props
     */
  >(undefined)
  /**
   * Whether to automatically manage layering.
   * @group Props
   */
  readonly autoZIndex = input<boolean, unknown>(
    true,
    /**
     * Base zIndex value to use in layering.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Base zIndex value to use in layering.
   * @group Props
   */
  readonly baseZIndex = input<number, unknown>(
    0,
    /**
     * Transition options of the show animation.
     * @group Props
     */
    { transform: numberAttribute },
  )
  /**
   * Transition options of the show animation.
   * @group Props
   */
  readonly showTransitionOptions = input<string>(
    '.12s cubic-bezier(0, 0, 0.2, 1)',
    /**
     * Transition options of the hide animation.
     * @group Props
     */
  )
  /**
   * Transition options of the hide animation.
   * @group Props
   */
  readonly hideTransitionOptions = input<string>(
    '.1s linear',
    /**
     * Defines a string value that labels an interactive element.
     * @group Props
     */
  )
  /**
   * Defines a string value that labels an interactive element.
   * @group Props
   */
  readonly ariaLabel = input<
    string | undefined
    /**
     * Identifier of the underlying input element.
     * @group Props
     */
  >(undefined)
  /**
   * Identifier of the underlying input element.
   * @group Props
   */
  readonly ariaLabelledBy = input<
    string | undefined
    /**
     * Current id state as a string.
     * @group Props
     */
  >(undefined)
  /**
   * Current id state as a string.
   * @group Props
   */
  readonly id = input<
    string | undefined
    /**
     * Index of the element in tabbing order.
     * @group Props
     */
  >(UniqueComponentId())
  /**
   * Index of the element in tabbing order.
   * @group Props
   */
  readonly tabindex = input<number, unknown>(
    0,
    /**
     * Callback to invoke when overlay menu is shown.
     * @group Emits
     */
    { transform: numberAttribute },
  )
  /**
   * Callback to invoke when overlay menu is shown.
   * @group Emits
   */
  readonly onShow = output<any>()
  /**
   * Callback to invoke when overlay menu is hidden.
   * @group Emits
   */
  readonly onHide = output<any>()
  /**
   * Callback to invoke when the list loses focus.
   * @param {Event} event - blur event.
   * @group Emits
   */
  readonly onBlur = output<Event>()
  /**
   * Callback to invoke when the list receives focus.
   * @param {Event} event - focus event.
   * @group Emits
   */
  readonly onFocus = output<Event>()

  @ViewChild('list') listViewChild: Nullable<ElementRef>

  @ViewChild('container') containerViewChild: Nullable<ElementRef>

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined

  startTemplate: TemplateRef<any> | undefined

  endTemplate: TemplateRef<any> | undefined

  itemTemplate: TemplateRef<any> | undefined

  submenuHeaderTemplate: TemplateRef<any> | undefined

  container: HTMLDivElement | undefined

  scrollHandler: ConnectedOverlayScrollHandler | null | undefined

  documentClickListener: VoidListener

  documentResizeListener: VoidListener

  preventDocumentDefault: boolean | undefined

  target: any

  visible: boolean | undefined

  focusedOptionId = computed(() => {
    return this.focusedOptionIndex() !== -1 ? this.focusedOptionIndex() : null
  })

  public focusedOptionIndex: any = signal<any>(-1)

  public selectedOptionIndex: any = signal<any>(-1)

  public focused: boolean | undefined = false

  public overlayVisible: boolean | undefined = false

  relativeAlign: boolean | undefined

  /**
   * Toggles the visibility of the popup menu.
   * @param {Event} event - Browser event.
   * @group Method
   */
  public toggle(event: Event) {
    if (this.visible) {
      this.hide()
    } else {
      this.show(event)
    }

    this.preventDocumentDefault = true
  }
  /**
   * Displays the popup menu.
   * @param {Event} event - Browser event.
   * @group Method
   */
  public show(event: any) {
    if (this.visible && this.target !== event.currentTarget) {
      this.hide()
    }

    this.target = event.currentTarget
    this.relativeAlign = event.relativeAlign
    this.visible = true
    this.preventDocumentDefault = true
    this.overlayVisible = true
    this.cd.detectChanges()
  }

  ngOnInit() {
    if (!this.popup()) {
      this.bindDocumentClickListener()
    }
  }

  ngAfterContentInit() {
    this.templates?.forEach((item) => {
      switch (item.getType()) {
        case 'start':
          this.startTemplate = item.template
          break

        case 'end':
          this.endTemplate = item.template
          break

        case 'itemTemplate':
          this.itemTemplate = item.template
          break

        case 'submenuheader':
          this.submenuHeaderTemplate = item.template
          break

        default:
          this.itemTemplate = item.template
          break
      }
    })
  }

  getTabIndexValue(): string | null {
    const tabindex = this.tabindex()
    return tabindex !== undefined ? tabindex.toString() : null
  }

  /**
   * Starts `false` on every fresh mount so the enter transition has a genuine "before" class
   * state to animate from; flipped to `true` once (animate.enter) fires.
   */
  menuOverlayEntered = signal(false)

  onOverlayEnter(event: AnimationCallbackEvent) {
    if (this.popup()) {
      this.container = event.target as HTMLDivElement
      this.moveOnTop()
      this.onShow.emit({})
      this.appendOverlay()
      this.alignOverlay()
      this.bindDocumentClickListener()
      this.bindDocumentResizeListener()
      this.bindScrollListener()
      DomHandler.focus(this.listViewChild.nativeElement)
      this.preventDocumentDefault = true
    }

    this.menuOverlayEntered.set(true)

    DomHandler.onTransitionEnd(event.target, () => event.animationComplete())
  }

  onOverlayLeave(event: AnimationCallbackEvent) {
    this.onOverlayHide()
    this.onHide.emit({})

    DomHandler.onTransitionEnd(event.target, () => {
      if (this.autoZIndex()) {
        ZIndexUtils.clear(event.target)
      }
      this.menuOverlayEntered.set(false)
      event.animationComplete()
    })
  }

  alignOverlay() {
    if (this.relativeAlign) {
      DomHandler.relativePosition(this.container, this.target)
    } else {
      DomHandler.absolutePosition(this.container, this.target)
    }
  }

  appendOverlay() {
    const appendTo = this.appendTo()
    if (appendTo) {
      if (appendTo === 'body') {
        this.renderer.appendChild(this.document.body, this.container)
      } else {
        DomHandler.appendChild(this.container, appendTo)
      }
    }
  }

  restoreOverlayAppend() {
    if (this.container && this.appendTo()) {
      this.renderer.appendChild(this.el.nativeElement, this.container)
    }
  }

  moveOnTop() {
    if (this.autoZIndex()) {
      ZIndexUtils.set('menu', this.container, this.baseZIndex() + this.config.zIndex.menu)
    }
  }
  /**
   * Hides the popup menu.
   * @group Method
   */
  public hide() {
    this.visible = false
    this.relativeAlign = false
    this.cd.detectChanges()
  }

  onWindowResize() {
    if (this.visible && !DomHandler.isTouchDevice()) {
      this.hide()
    }
  }

  menuitemId(item: MenuItem, id: string, index?: number, childIndex?: number) {
    return item?.id ?? `${id}_${index}${childIndex !== undefined ? '_' + childIndex : ''}`
  }

  isItemFocused(id) {
    return this.focusedOptionId() === id
  }

  label(label: any) {
    return typeof label === 'function' ? label() : label
  }

  disabled(disabled: any) {
    return typeof disabled === 'function'
      ? disabled()
      : typeof disabled === 'undefined'
        ? false
        : disabled
  }

  activedescendant() {
    return this.focused ? this.focusedOptionId() : undefined
  }

  onListFocus(event: Event) {
    if (!this.focused) {
      this.focused = true
      this.onFocus.emit(event)
    }
  }

  onListBlur(event: FocusEvent | MouseEvent) {
    if (this.focused) {
      this.focused = false
      this.changeFocusedOptionIndex(-1)
      this.selectedOptionIndex.set(-1)
      this.focusedOptionIndex.set(-1)
      this.onBlur.emit(event)
    }
  }

  onListKeyDown(event) {
    switch (event.code) {
      case 'ArrowDown':
        this.onArrowDownKey(event)
        break

      case 'ArrowUp':
        this.onArrowUpKey(event)
        break

      case 'Home':
        this.onHomeKey(event)
        break

      case 'End':
        this.onEndKey(event)
        break

      case 'Enter':
        this.onEnterKey(event)
        break

      case 'NumpadEnter':
        this.onEnterKey(event)
        break

      case 'Space':
        this.onSpaceKey(event)
        break

      case 'Escape':
      case 'Tab':
        if (this.popup()) {
          DomHandler.focus(this.target)
          this.hide()
        }
        this.overlayVisible && this.hide()
        break

      default:
        break
    }
  }

  onArrowDownKey(event) {
    const optionIndex = this.findNextOptionIndex(this.focusedOptionIndex())
    this.changeFocusedOptionIndex(optionIndex)
    event.preventDefault()
  }

  onArrowUpKey(event) {
    if (event.altKey && this.popup()) {
      DomHandler.focus(this.target)
      this.hide()
      event.preventDefault()
    } else {
      const optionIndex = this.findPrevOptionIndex(this.focusedOptionIndex())

      this.changeFocusedOptionIndex(optionIndex)
      event.preventDefault()
    }
  }

  onHomeKey(event) {
    this.changeFocusedOptionIndex(0)
    event.preventDefault()
  }

  onEndKey(event) {
    this.changeFocusedOptionIndex(
      DomHandler.find(
        this.containerViewChild.nativeElement,
        'li[data-pc-section="menuitem"][data-p-disabled="false"]',
      ).length - 1,
    )
    event.preventDefault()
  }

  onEnterKey(event) {
    const element = DomHandler.findSingle(
      this.containerViewChild.nativeElement,
      `li[id="${`${this.focusedOptionIndex()}`}"]`,
    )
    const anchorElement = element && DomHandler.findSingle(element, 'a')

    this.popup() && DomHandler.focus(this.target)
    anchorElement ? anchorElement.click() : element && element.click()

    event.preventDefault()
  }

  onSpaceKey(event) {
    this.onEnterKey(event)
  }

  findNextOptionIndex(index) {
    const links = DomHandler.find(
      this.containerViewChild.nativeElement,
      'li[data-pc-section="menuitem"][data-p-disabled="false"]',
    )
    const matchedOptionIndex = [...links].findIndex((link) => link.id === index)

    return matchedOptionIndex > -1 ? matchedOptionIndex + 1 : 0
  }

  findPrevOptionIndex(index) {
    const links = DomHandler.find(
      this.containerViewChild.nativeElement,
      'li[data-pc-section="menuitem"][data-p-disabled="false"]',
    )
    const matchedOptionIndex = [...links].findIndex((link) => link.id === index)

    return matchedOptionIndex > -1 ? matchedOptionIndex - 1 : 0
  }

  changeFocusedOptionIndex(index) {
    const links = DomHandler.find(
      this.containerViewChild.nativeElement,
      'li[data-pc-section="menuitem"][data-p-disabled="false"]',
    )

    if (links.length > 0) {
      const order = index >= links.length ? links.length - 1 : index < 0 ? 0 : index
      order > -1 && this.focusedOptionIndex.set(links[order].getAttribute('id'))
    }
  }

  itemClick(event: any, id: string) {
    const { originalEvent, item } = event

    if (!this.focused) {
      this.focused = true
      this.onFocus.emit(originalEvent)
    }

    if (item.disabled) {
      originalEvent.preventDefault()
      return
    }

    if (!item.url && !item.routerLink) {
      originalEvent.preventDefault()
    }

    if (item.command) {
      item.command({
        originalEvent: originalEvent,
        item: item,
      })
    }

    const popup = this.popup()
    if (popup) {
      this.hide()
    }

    if (!popup && this.focusedOptionIndex() !== id) {
      this.focusedOptionIndex.set(id)
    }
  }

  onOverlayClick(event: Event) {
    if (this.popup()) {
      this.overlayService.add({
        originalEvent: event,
        target: this.el.nativeElement,
      })
    }

    this.preventDocumentDefault = true
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener && isPlatformBrowser(this.platformId)) {
      const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document'

      this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
        const eventTarget = event.composed ? event.composedPath()[0] : event.target
        const isOutsideContainer =
          this.containerViewChild?.nativeElement &&
          !this.containerViewChild?.nativeElement.contains(eventTarget)
        const isOutsideTarget = !(
          this.target &&
          (this.target === eventTarget || this.target.contains(eventTarget))
        )
        if (!this.popup() && isOutsideContainer && isOutsideTarget) {
          this.onListBlur(event)
        }
        if (
          this.preventDocumentDefault &&
          this.overlayVisible &&
          isOutsideContainer &&
          isOutsideTarget
        ) {
          this.hide()
          this.preventDocumentDefault = false
        }
      })
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener()
      this.documentClickListener = null
    }
  }

  bindDocumentResizeListener() {
    if (!this.documentResizeListener && isPlatformBrowser(this.platformId)) {
      const window = this.document.defaultView
      this.documentResizeListener = this.renderer.listen(
        window,
        'resize',
        this.onWindowResize.bind(this),
      )
    }
  }

  unbindDocumentResizeListener() {
    if (this.documentResizeListener) {
      this.documentResizeListener()
      this.documentResizeListener = null
    }
  }

  bindScrollListener() {
    if (!this.scrollHandler && isPlatformBrowser(this.platformId)) {
      this.scrollHandler = new ConnectedOverlayScrollHandler(this.target, () => {
        if (this.visible) {
          this.hide()
        }
      })
    }

    this.scrollHandler?.bindScrollListener()
  }

  unbindScrollListener() {
    if (this.scrollHandler) {
      this.scrollHandler.unbindScrollListener()
    }
  }

  onOverlayHide() {
    this.unbindDocumentClickListener()
    this.unbindDocumentResizeListener()
    this.unbindScrollListener()
    this.preventDocumentDefault = false
  }

  ngOnDestroy() {
    const popup = this.popup()
    if (popup) {
      if (this.scrollHandler) {
        this.scrollHandler.destroy()
        this.scrollHandler = null
      }

      if (this.container && this.autoZIndex()) {
        ZIndexUtils.clear(this.container)
      }

      this.restoreOverlayAppend()
      this.onOverlayHide()
    }

    if (!popup) {
      this.unbindDocumentClickListener()
    }
  }

  hasSubMenu(): boolean {
    const model = this.model()
    if (model) {
      for (const item of model) {
        if (item.items) {
          return true
        }
      }
    }
    return false
  }

  isItemHidden(item: any): boolean {
    if (item.separator) {
      return (
        item.visible === false ||
        (item.items && item.items.some((subitem) => subitem.visible !== false))
      )
    }
    return item.visible === false
  }
}
